
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import { ManuscriptClient, AssistClient } from "@/lib/clients";
import { logError, logInfo, logWarn } from "@/lib/utils/logger";

import {
  writerTypeToAssistWriterType,
  ClientPlatform,
  AssistWriterType,
  FeedbackManuscriptMode,
} from "@/lib/models/assist";
import { Manuscript } from "@/lib/models";
import ManuscriptListItem from "@/components/atoms/ManuscriptListItem.vue";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import { AssistAnalytics } from "@/lib/utils/analytics/assist";
import { ASSIST_MESSAGES } from "@/constants/assistMessages";

// 1リクエストあたりの最小文字数
const MIN_CONTENT_LENGTH = 100;
// 1リクエストあたりの最大文字数
const MAX_CONTENT_LENGTH = 200000;

// コンポーネント内で使用する表示用の原稿データ型
interface DisplayManuscript {
  id: string;
  title: string;
  characterCount: number;
  novelId: string;
  key?: string; // API取得時の原稿キー
}

const manuscriptClient = new ManuscriptClient();
const assistClient = new AssistClient();

const platform = ClientPlatform.WEB;

export default Vue.extend({
  name: "ManuscriptSelectContent",
  components: {
    ManuscriptListItem,
    ChatMessage,
  },
  props: {
    novelId: {
      type: String,
      default: null,
    },
    manuscripts: {
      type: Array as () => DisplayManuscript[],
      default: () => [],
    },
    useApi: {
      type: Boolean,
      default: true,
    },
    requestType: {
      type: String as () => "feedback" | "matching" | "catchcopy" | null,
      default: null,
    },
  },
  data() {
    return {
      selectedManuscriptIds: [] as string[], // 選択された原稿IDの配列（選択順序を保持）
      apiManuscripts: [] as DisplayManuscript[],
      localIsLoading: false,
      localError: null as string | null,
      // AI依頼処理状態管理
      requestProcessing: false,
      // アナリティクス
      analytics: null as AssistAnalytics | null,
      minContentLength: MIN_CONTENT_LENGTH,
      maxContentLength: MAX_CONTENT_LENGTH,
    };
  },
  computed: {
    // VuexストアからmanuscriptDataを取得
    vuexManuscriptData(): any[] | null {
      return this.$store.getters["assistSelectManuscriptModule/manuscriptData"];
    },
    vuexApiLoading(): boolean {
      return this.$store.getters["assistSelectManuscriptModule/isApiLoading"];
    },
    vuexApiError(): string | null {
      return this.$store.getters["assistSelectManuscriptModule/apiError"];
    },

    effectiveManuscripts(): DisplayManuscript[] {
      // VuexストアのデータとローカルAPIデータを統合
      if (this.vuexManuscriptData && this.vuexManuscriptData.length > 0) {
        // Vuexにデータがある場合はそれを使用（Phase 3で取得済み）
        return this.vuexManuscriptData.map((manuscript: any, index: number) => ({
          id: manuscript.id,
          key: manuscript.id,
          title: manuscript.title,
          characterCount: manuscript.characterCount,
          novelId: this.novelId,
        })) as DisplayManuscript[];
      }
      // フォールバック：従来のAPIデータを使用
      return this.apiManuscripts;
    },

    // ローディング状態とエラー状態をVuexと統合
    isLoading(): boolean {
      return this.vuexApiLoading || this.localIsLoading;
    },
    error(): string | null {
      return this.vuexApiError || this.localError;
    },

    filteredManuscripts(): DisplayManuscript[] {
      if (!this.novelId) {
        // novelIdが指定されていない場合は全ての原稿を表示
        return this.effectiveManuscripts;
      }
      // novelIdが指定されている場合は該当する原稿のみをフィルタリング
      return this.effectiveManuscripts.filter((manuscript) => manuscript.novelId === this.novelId);
    },
    selectedManuscripts(): DisplayManuscript[] {
      return this.selectedManuscriptIds
        .map((id) => this.effectiveManuscripts.find((m) => m.id === id))
        .filter((m) => m !== undefined) as DisplayManuscript[];
    },
    selectedCharacterCount(): number {
      return this.selectedManuscripts.reduce((sum, manuscript) => sum + manuscript.characterCount, 0);
    },
    totalFishCost(): string {
      return this.totalFishCostNumber.toLocaleString("ja-JP");
    },
    isApplyDryModeExtraFish(): boolean {
      return this.requestType === "feedback" && this.isDryMode;
    },
    totalFishCostNumber(): number {
      if (this.isApplyDryModeExtraFish) {
        return this.selectedCharacterCount + this.dryModeExtraFish;
      }
      return this.selectedCharacterCount;
    },
    formattedDryModeExtraFish(): string | null {
      if (!this.isApplyDryModeExtraFish || this.dryModeExtraFish <= 0) {
        return null;
      }
      const locale = this.$i18n?.locale ?? "ja-JP";
      return this.dryModeExtraFish.toLocaleString(locale);
    },
    // Fish残高チェック関連の計算プロパティを追加
    isFishInsufficient(): boolean {
      if (this.selectedManuscriptIds.length === 0) return false;
      const canConsumeFn = this.canConsumeFish;
      if (typeof canConsumeFn === "function") {
        return !canConsumeFn(this.totalFishCostNumber);
      }
      // フォールバック: 直接残高比較
      return this.currentFish < this.totalFishCostNumber;
    },
    // 最小文字数の不足チェック
    isInsufficientMinContentLength(): boolean {
      return this.selectedCharacterCount < MIN_CONTENT_LENGTH;
    },
    // 最大文字数の超過チェック
    isExceededMaxContentLength(): boolean {
      return this.selectedCharacterCount > MAX_CONTENT_LENGTH;
    },
    fishShortage(): number {
      if (!this.isFishInsufficient) return 0;
      return this.totalFishCostNumber - this.currentFish;
    },
    isRequestButtonDisabled(): boolean {
      return (
        this.selectedManuscriptIds.length === 0 ||
        this.isLoading ||
        this.isFishInsufficient ||
        this.isInsufficientMinContentLength ||
        this.isExceededMaxContentLength ||
        this.requestProcessing
      );
    },
    // 警告メッセージ
    warningMessage(): string {
      if (this.isExceededMaxContentLength) {
        return "最大文字数を超えています";
      }
      if (this.isFishInsufficient) {
        return "Fish が不足しています";
      }
      if (this.isInsufficientMinContentLength) {
        return "最小文字数を下回っています";
      }
      return "";
    },
    // 依頼ボタンテキストの動的表示
    requestButtonText(): string {
      if (this.requestProcessing) {
        if (this.requestType === "matching") {
          return ASSIST_MESSAGES.MATCHING_COMPANY.loading;
        }
        if (this.requestType === "catchcopy") {
          return ASSIST_MESSAGES.GENERATE_CATCHCOPY.loading;
        }
        return ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.loading;
      }
      if (this.selectedManuscriptIds.length === 0) {
        return "原稿を選択してください";
      }
      if (this.isExceededMaxContentLength) {
        return "最大文字数を超えています";
      }
      if (this.isFishInsufficient) {
        return "Fish が不足しています";
      }
      if (this.isInsufficientMinContentLength) {
        return "最小文字数を下回っています";
      }
      return "依頼する";
    },
    // 明示的な参照で確実に取得
    canConsumeFish() {
      return this.$store.getters["fishModule/canConsumeFish"];
    },
    currentFish() {
      return this.$store.getters["fishModule/currentFish"];
    },
    // Vuexからアシスタント猫のマスタを取得
    ...mapGetters("assistantCatModule", {
      assistantCatMaster: "assistantCatMaster",
      dryModeExtraFish: "dryModeExtraFish",
    }),
    // 現在の感想モード
    currentFeedbackManuscriptMode(): FeedbackManuscriptMode {
      return this.assistantCatMaster?.feedbackManuscriptMode ?? FeedbackManuscriptMode.NORMAL;
    },
    isDryMode(): boolean {
      return this.currentFeedbackManuscriptMode === FeedbackManuscriptMode.DRY;
    },
    // 中間コンテンツ（Fish情報など）があるかどうかを判定
    hasMiddleContent(): boolean {
      return this.selectedManuscriptIds.length > 0;
    },
    ...mapGetters("writerTypeResultModule", ["writerType"]),
    // ローディングメッセージ（AI依頼種別に応じて）
    loadingMessage(): string {
      if (this.requestType === "matching") {
        return ASSIST_MESSAGES.MATCHING_COMPANY.loading;
      }
      if (this.requestType === "catchcopy") {
        return ASSIST_MESSAGES.GENERATE_CATCHCOPY.loading;
      }
      return ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.loading;
    },
  },
  watch: {
    novelId: {
      immediate: true,
      async handler(newNovelId: string | null) {
        // VuexでURL監視によりAPI呼び出しが行われるため、ここでは重複呼び出しを避ける
        if (!newNovelId) {
          // novelIdがない場合はデータをクリア
          this.apiManuscripts = [];
          this.selectedManuscriptIds = [];
          return;
        }

        // Vuexストアにデータがない場合のみローカルAPI呼び出し（フォールバック）
        if (this.useApi && !this.vuexManuscriptData) {
          await this.fetchManuscriptsFromApi(newNovelId);
        }
      },
    },
    useApi: {
      immediate: true,
      async handler(newUseApi: boolean) {
        // useApiの変更時の処理も同様に調整
        if (newUseApi && this.novelId && !this.vuexManuscriptData) {
          await this.fetchManuscriptsFromApi(this.novelId);
        }
      },
    },
  },
  mounted() {
    // Fish データの初期化を確実に実行
    this.$store.dispatch("fishModule/fetchFishData");
    this.loadDryModeExtraFish();

    // アナリティクス初期化
    this.analytics = new AssistAnalytics(this.$gtm);
  },
  methods: {
    ...mapActions("fishModule", ["consumeFishForRequest"]),
    async fetchManuscriptsFromApi(novelId: string): Promise<void> {
      this.localIsLoading = true;
      this.localError = null;
      this.selectedManuscriptIds = []; // 選択状態をリセット

      try {
        // 原稿一覧を取得
        const apiManuscripts: Manuscript[] = await manuscriptClient.fetchAllManuscript(novelId);

        if (!apiManuscripts || apiManuscripts.length === 0) {
          this.apiManuscripts = [];
          this.$emit("manuscripts-loaded", this.apiManuscripts);
          return;
        }

        // 各原稿の詳細情報を並列取得
        const manuscriptPromises = apiManuscripts.map(async (manuscript, index) => {
          const manuscriptKey = manuscript.key;
          let characterCount = 0;

          try {
            // 原稿の文字数を取得
            if (manuscriptKey) {
              const content = await manuscriptClient.fetchManuscriptContent(novelId, manuscriptKey);
              if (content) {
                characterCount = content.length;
              }
            }
          } catch (detailError) {
            logWarn(
              { module: "ManuscriptSelectContent", action: "fetchManuscriptDetails" },
              `原稿 ${manuscriptKey} の詳細情報取得に失敗`,
              detailError
            );
            // 詳細情報の取得に失敗してもリストには含める
          }

          return {
            id: manuscriptKey || `manuscript-${index + 1}`,
            key: manuscriptKey,
            title: manuscript.title || `原稿${index + 1}`,
            characterCount,
            novelId,
          } as DisplayManuscript;
        });

        this.apiManuscripts = await Promise.all(manuscriptPromises);
        this.$emit("manuscripts-loaded", this.apiManuscripts);
      } catch (error) {
        logError(
          { module: "ManuscriptSelectContent", action: "fetchManuscriptsFromApi" },
          "原稿一覧の取得に失敗しました",
          error
        );
        this.localError = "原稿一覧の取得に失敗しました。再度お試しください。";
        this.apiManuscripts = [];
        this.$emit("manuscripts-error", error);
      } finally {
        this.localIsLoading = false;
      }
    },
    handleManuscriptToggle(manuscript: DisplayManuscript): void {
      // 文字数が0の場合は選択できない
      if (manuscript.characterCount === 0) {
        return;
      }

      const index = this.selectedManuscriptIds.indexOf(manuscript.id);
      if (index > -1) {
        // 既に選択されている場合は選択解除
        this.selectedManuscriptIds.splice(index, 1);
      } else {
        // 選択されていない場合は選択に追加
        this.selectedManuscriptIds.push(manuscript.id);
      }

      this.$emit("manuscript-select", {
        manuscript,
        selectedManuscripts: this.selectedManuscripts,
        selectionOrder: this.selectedManuscriptIds.length,
      });
    },
    async handleConfirmSelection(): Promise<void> {
      logInfo({ module: "ManuscriptSelectContent", action: "handleConfirmSelection" }, "AI感想依頼処理開始");
      if (this.selectedManuscriptIds.length === 0) {
        return;
      }

      // 依頼処理状態の管理
      this.localIsLoading = true;
      this.requestProcessing = true; // AI依頼処理開始
      this.localError = null;

      try {
        // 1. 選択原稿の検証
        const { selectedManuscripts } = this;
        if (selectedManuscripts.length === 0) {
          throw new Error("選択された原稿がありません。");
        }

        // 2. Fish消費量の取得 (ボタン無効化により不足チェック済み)
        const fishCost = this.totalFishCostNumber;

        // 3. Fish消費実行
        const consumeSuccess = await this.consumeFishForRequest(fishCost);
        if (!consumeSuccess) {
          console.error("Fish消費失敗:", { required: fishCost });
          this.localError = "Fish の消費に失敗しました。再度お試しください。";
          this.$emit("fish-consume-failed", {
            required: fishCost,
            message: this.localError,
          });
          return;
        }

        // 4. アシスタント猫の選択状態を確認（優先）
        const selectedCatType = this.$store.getters["assistantCatModule/selectedType"];

        let assistWriterType: AssistWriterType;
        let analyticsWriterType: string;

        if (selectedCatType) {
          // ユーザーが明示的に選択したアシスタント猫を優先使用
          assistWriterType = selectedCatType;
          analyticsWriterType = assistWriterType;
          logInfo(
            { module: "ManuscriptSelectContent", action: "getAssistWriterType" },
            "選択されたアシスタント猫を使用",
            { assistWriterType }
          );
        } else {
          // フォールバック: 作家タイプ診断の結果を使用
          const userWriterType = this.writerType;
          logInfo({ module: "ManuscriptSelectContent", action: "getWriterType" }, "WriterType取得完了", {
            writerType: userWriterType,
          });

          assistWriterType = writerTypeToAssistWriterType(userWriterType);
          console.log("変換後のAssistWriterType:", assistWriterType);

          // アナリティクス用のWriterType（RANDOMフラグを考慮）
          const isRandomWriterType = this.$store.getters["writerTypeResultModule/isRandom"];
          analyticsWriterType = isRandomWriterType ? "RANDOM" : assistWriterType;

          if (isRandomWriterType) {
            logInfo(
              { module: "ManuscriptSelectContent", action: "getWriterType" },
              "ランダム作家タイプのためアナリティクスにRANDOMを送信"
            );
          }
        }

        // 5. 原稿キー配列の準備
        const manuscriptKeys = selectedManuscripts.map((manuscript) => manuscript.key || manuscript.id);

        // 6. リクエスト種別に応じたAPI呼び出し
        if (this.requestType === "matching") {
          // 応募先検索依頼
          const { requestTitle } = ASSIST_MESSAGES.MATCHING_COMPANY;
          await assistClient.createMatchingCompany({
            novelId: this.novelId,
            manuscriptKeys,
            writerType: assistWriterType,
            requestTitle,
            platform,
          });

          // アナリティクス: 応募先検索イベント送信
          if (this.analytics) {
            const totalCharacters = selectedManuscripts.reduce((sum, manuscript) => sum + manuscript.characterCount, 0);
            this.analytics.trackRequestSubmit(
              analyticsWriterType,
              selectedManuscripts.length,
              totalCharacters,
              this.novelId,
              "matching"
            );
          }
        } else if (this.requestType === "catchcopy") {
          // キャッチコピー生成依頼
          const { requestTitle } = ASSIST_MESSAGES.GENERATE_CATCHCOPY;
          await assistClient.createGenerateCatchcopy({
            novelId: this.novelId,
            manuscriptKeys,
            writerType: assistWriterType,
            requestTitle,
            platform,
          });

          // アナリティクス: キャッチコピー生成イベント送信
          if (this.analytics) {
            const totalCharacters = selectedManuscripts.reduce((sum, manuscript) => sum + manuscript.characterCount, 0);
            this.analytics.trackRequestSubmit(
              analyticsWriterType,
              selectedManuscripts.length,
              totalCharacters,
              this.novelId,
              "catchcopy"
            );
          }
        } else {
          // 感想依頼（デフォルト）
          const { requestTitle } = ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT;
          await assistClient.createFeedbackManuscript({
            novelId: this.novelId,
            manuscriptKeys,
            writerType: assistWriterType,
            feedbackManuscriptMode: this.currentFeedbackManuscriptMode,
            requestTitle,
            platform,
          });

          // アナリティクス: 感想依頼イベント送信
          if (this.analytics) {
            const totalCharacters = selectedManuscripts.reduce((sum, manuscript) => sum + manuscript.characterCount, 0);
            this.analytics.trackRequestSubmit(
              analyticsWriterType,
              selectedManuscripts.length,
              totalCharacters,
              this.novelId,
              "feedback"
            );
          }
        }

        // 10. 成功時: 履歴画面への遷移
        await this.$store.dispatch("assistNavigationModule/showHistory");
      } catch (error: any) {
        console.error("AI感想依頼処理でエラー:", error);

        // エラーの種類に応じたメッセージ設定
        let errorMessage = "予期しないエラーが発生しました。";

        if (error.message) {
          if (error.message.includes("Fish") || error.message.includes("残高")) {
            errorMessage = "Fish が不足しています。";
          } else if (error.message.includes("GPT Handler") || error.message.includes("Lambda")) {
            errorMessage = "AI処理の実行に失敗しました。しばらく時間をおいて再度お試しください。";
          } else if (error.message.includes("401") || error.message.includes("認証")) {
            errorMessage = "認証が必要です。再度ログインしてください。";
          } else if (error.message.includes("ネットワーク") || error.message.includes("network")) {
            errorMessage = "通信エラーが発生しました。接続を確認してください。";
          } else {
            errorMessage = error.message;
          }
        }

        this.localError = errorMessage;

        this.$emit("request-failed", {
          error,
          message: errorMessage,
          canRetry: !errorMessage.includes("認証") && !errorMessage.includes("Fish"),
        });
      } finally {
        this.localIsLoading = false;
        this.requestProcessing = false; // AI依頼処理終了
      }
    },
    isSelected(manuscriptId: string): boolean {
      return this.selectedManuscriptIds.includes(manuscriptId);
    },
    getSelectionOrder(manuscriptId: string): number {
      return this.selectedManuscriptIds.indexOf(manuscriptId) + 1;
    },
    formatCharacterCount(count: number): string {
      return count.toLocaleString("ja-JP");
    },
    handleRetry(): void {
      this.localError = null;
      this.fetchManuscriptsFromApi(this.novelId);
    },
    clearVuexError(): void {
      this.$store.dispatch("assistSelectManuscriptModule/clearApiError");
    },
    async loadDryModeExtraFish(): Promise<void> {
      try {
        await this.$store.dispatch("assistantCatModule/fetchDryModeExtraFish");
      } catch (error) {
        console.error("fetchDryModeExtraFish failed:", error);
      }
    },
    async handleRefreshManuscripts(): Promise<void> {
      if (!this.novelId) return;

      try {
        // Vuexストアの原稿データを再取得
        await this.$store.dispatch("assistSelectManuscriptModule/fetchManuscriptData", this.novelId);
      } catch (error) {
        logError(
          { module: "ManuscriptSelectContent", action: "handleRefreshManuscripts" },
          "原稿一覧の再読み込みに失敗",
          error
        );
        this.localError = "原稿一覧の再読み込みに失敗しました。";
      }
    },
  },
});
